import { useCallback, useEffect, useState } from "react";
import { BASE_API_URL } from "../config";

/** Where a share token opens. */
export const sharedPaperUrl = (token) => `${window.location.origin}/shared/paper/${token}`;

/**
 * The owner's view of how a paper is shared: the link and whether it is on,
 * and who has been added.
 *
 * Every change returns the whole state from the server, which is simply put
 * in place — there is nothing to reconcile, and the list never shows someone
 * the server did not accept.
 */
export function usePaperShare({ attemptGuid, enabled }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const call = useCallback(
    async (path = "", { method = "GET", body } = {}) => {
      const token = localStorage.getItem("accessToken");
      const res = await fetch(`${BASE_API_URL}/api/v1/paper-shares/${attemptGuid}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(body ? { "Content-Type": "application/json" } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail = typeof data.detail === "string" ? data.detail : "Something went wrong. Try again.";
        throw new Error(detail);
      }
      setState(data);
      return data;
    },
    [attemptGuid]
  );

  useEffect(() => {
    if (!enabled || !attemptGuid) return;
    setLoading(true);
    setError(null);
    call()
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [enabled, attemptGuid, call]);

  const setLinkEnabled = useCallback(
    (on) => call("/link", { method: "PUT", body: { enabled: on } }),
    [call]
  );
  const addCollaborator = useCallback(
    (email) => call("/collaborators", { method: "POST", body: { email } }),
    [call]
  );
  const removeCollaborator = useCallback(
    (userGuid) => call(`/collaborators/${userGuid}`, { method: "DELETE" }),
    [call]
  );

  return { state, loading, error, setLinkEnabled, addCollaborator, removeCollaborator };
}
